<template>
  <div class="page__wrapper">
    <div
      v-if="svgMap.length > 0"
      ref="mapGrid"
      class="container-static"
    >
      <div
        v-if="!aimingField"
        ref="mapSearch"
        class="map-search__wrapper"
      >
        <map-search-view
          :directory="directory"
          :stand-numbers="standNumbers"
          @select="selectFound"
        />
        <button
          v-if="navRouter"
          type="button"
          class="map-route-btn"
          :aria-label="$t('map.routeOpen')"
          @click="openRouteForm()"
        >
          <i class="exu-route" />
        </button>
      </div>
      <map-item-view
        v-for="(itemMap, index) in svgMap"
        :key="`map_${index}`"
        :id="`map_${index}`"
        :ref="`mapRef_${index}`"
        :start-stand-id="showStandId"
        :map="itemMap"
        :aiming="!!aimingField && index === 0"
        @click-stand="clickStand"
        @ready="onMapReady(index)"
      />
    </div>
    <div
      v-if="aimingField"
      ref="bottomPanel"
      class="participant-info__wrapper"
    >
      <div class="map-aim-bar">
        <span class="map-aim-bar__hint">
          {{ aimedTarget ? aimedLabel : $t('map.aimHint') }}
        </span>
        <div class="up-btn__line base-3-6-3">
          <button
            type="button"
            class="up-btn up-btn-primary"
            @click="aimingField = null"
          >
            {{ $t('map.aimCancel') }}
          </button>
          <button
            type="button"
            class="up-btn"
            :class="aimedTarget ? 'up-btn-accent' : 'up-btn-disabled'"
            :disabled="!aimedTarget"
            @click="confirmAim"
          >
            {{ $t('map.aimConfirm') }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-else-if="routeFormOpen"
      ref="bottomPanel"
      class="participant-info__wrapper"
    >
      <route-form-view
        :places="routePlaces"
        :from="routeFrom"
        :to="routeTo"
        :collapsed="routeCollapsed"
        :message="routeMessage"
        @update:from="routeFrom = $event"
        @update:to="routeTo = $event"
        @build="buildRoute"
        @touch="routeMessage = null"
        @aim="startAiming"
        @expand="routeCollapsed = false"
        @close="closeRouteForm"
      />
    </div>
    <div
      v-else-if="standProps"
      ref="bottomPanel"
      class="participant-info__wrapper"
    >
      <div class="participant-info">
        <button
          @click="closeStandProps()"
          class="close-btn"
        >
          <i class="exu-x-close" />
        </button>
        <div class="participant-logo">
          <company-logo
            :src="standProps.logo"
            :alt="`${standProps.name}-logo`"
          />
        </div>
        <div class="participant-about">
          <span class="participant-about-stand">
            {{ $t('map.stand') }} {{ standProps.stand }}
          </span>
          <span class="participant-about-company">{{ standProps.name }}</span>
          <div class="participant-actions">
            <button
              v-if="standIsRoutable"
              type="button"
              class="participant-route"
              @click="openRouteForm(standProps.stand)"
            >
              <i class="exu-route" /> {{ $t('map.route') }}
            </button>
            <router-link
              v-if="profileRoute"
              :to="profileRoute"
              class="participant-link"
            >
            {{ $t('map.more') }} <i class="exu-chevron-right" />
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import appBaseNav from '@/components/mixins/appBaseNav';
import methodsmap from '@/components/mixins/apiMethodsMap';
import methodsExhibitors from '@/components/mixins/methodsExhibitors';
import MapItemView from '@/views/pages/MapView/MapItem.vue';
import MapSearchView from '@/views/pages/MapView/MapSearch.vue';
import RouteFormView from '@/views/pages/MapView/RouteForm.vue';
import { CompanyLogo } from '@/components/atoms';
import { createRouter } from '@/views/pages/MapView/navigation/router';
import checkPair from '@/views/pages/MapView/navigation/checkPair';
import {
  directoryFromStands,
  directoryFromExhibitors,
  normalizeCompany,
} from '@/views/pages/MapView/standsDirectory';
import { normalizeStandNumber } from '@/views/pages/MapView/standState';

/** На столько кнопки зума подняты в самой карте. */
const ZOOM_BASE_OFFSET = 122;
/** Верх верхней кнопки над низом карты: 168 её отступ плюс 36 высота. */
const ZOOM_STACK_TOP = 204;
/** Просвет между нижней панелью и кнопками. */
const PANEL_GAP = 10;

export default {
  name: 'MapPageView',
  mixins: [appBaseNav, methodsmap, methodsExhibitors],
  props: {
    stand_id: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      titleMap: null,
      svgMap: [],

      standsList: [],
      standInfo: [],

      fontsList: new Set(),

      showStand: null,
      standProps: null,

      // Роутер живёт один на загруженный граф: построение маршрута —
      // операция, а не инициализация.
      navRouter: null,

      // Справочник компаний и карта могут прийти в любом порядке
      directory: [],
      mapIsReady: false,

      // Номера стендов карты — для поиска стенда без компании
      standNumbers: [],

      // Форма маршрута
      routeFormOpen: false,
      routeCollapsed: false,
      routeFrom: { text: '', id: null },
      routeTo: { text: '', id: null },
      routeMessage: null,

      // Прицеливание: какое поле заполняем и что сейчас поймано
      aimingField: null,
      aimedTarget: null,
    };
  },
  components: {
    MapItemView, MapSearchView, RouteFormView, CompanyLogo,
  },
  computed: {
    showStandId() {
      return this.showStand || this.stand_id;
    },

    /**
     * Цели без номера: кафе, WC, входы. Стенды в список не попадают —
     * их полторы сотни, их вводят номером.
     */
    routePlaces() {
      if (!this.navRouter) return [];
      return this.navRouter.destinations().filter((target) => target.kind !== 'booth');
    },

    /**
     * Кнопки «Маршрут» нет, если стенда нет в графе: не кнопка, которая
     * ругается по нажатию, а её отсутствие.
     */
    /** Что сейчас лежит внизу — от этого зависит, куда поднять зум. */
    bottomPanelKind() {
      if (this.aimingField) return 'aim';
      if (this.routeFormOpen) return this.routeCollapsed ? 'strip' : 'form';
      return this.standProps ? 'card' : null;
    },

    /** Подпись пойманного: у стенда номер, у кафе и двери — название. */
    aimedLabel() {
      if (!this.aimedTarget) return '';

      const known = this.navRouter && this.navRouter.target(this.aimedTarget.id);
      return known ? known.label : this.aimedTarget.label;
    },

    standIsRoutable() {
      if (!this.navRouter || !this.standProps) return false;
      return !!this.navRouter.target(`stand-${this.standProps.stand}`);
    },

    /**
     * Переход в профиль компании. Модуль называет сама компания: за
     * стендом может стоять и партнёр, и экспонент, а `ident` у них из
     * разных нумераций — ошибиться модулем значит открыть чужую
     * компанию. Маршрута может не быть вовсе: модуль вырезают из
     * сборки под клиента. Тогда ссылки просто нет.
     */
    profileRoute() {
      if (!this.standProps) return null;

      // Модуль не назван — ведём в экспонентов, как вело всегда: это
      // запасной путь, у справочника из `stands` модуль есть у каждой
      const name = this.standProps.app || 'exhibitorProfile';
      const { ident } = this.standProps;
      if (!ident || !this.$router.hasRoute(name)) return null;

      return { name, params: { id: ident } };
    },
  },
  watch: {
    bottomPanelKind() {
      this.$nextTick(this.watchBottomPanel);
    },
    aimingField(value) {
      if (!value && this.aimFrame) {
        cancelAnimationFrame(this.aimFrame);
        this.aimFrame = null;
      }
    },
  },

  beforeUnmount() {
    if (this.panelObserver) this.panelObserver.disconnect();
    if (this.aimFrame) cancelAnimationFrame(this.aimFrame);
  },

  mounted() {
    // Намеренно мимо data(): наблюдатель и кадр не должны становиться
    // реактивными
    this.panelObserver = null;
    this.aimFrame = null;

    this.$store.commit('setPageTitle', 'Схема');
    this.loadPage();
    if (this.stand_id) {
      this.clickStand(this.stand_id);
    }
  },
  methods: {
    loadPage() {
      this.getMapPair()
        .then((data) => {
          if (data && data.length) {
            this.svgMap = [...data];
            for (let i = 0; i < this.svgMap.length; i += 1) {
              // this.svgMapWidth.push(100);
              this.standInfo.push(null);

              if (data[i].font_link && !this.fontsList.has(data[i].font_family)) {
                this.updateFonts(data[i].font_link);
                this.fontsList.add(data[i].font_family);
              }
            }
            this.initNavigation(this.svgMap[0]);
            this.loadDirectory();
          }
        });
    },

    /**
     * Справочник компаний. Его может не быть — тогда стенды остаются
     * такими, какими пришли с картой, а поиск по номеру работает по
     * графу.
     */
    loadDirectory() {
      // Справочник из ответа карты: он приехал вместе с ней, значит
      // номера в нём от неё, и подставлять ничего не надо
      const fromMap = directoryFromStands(this.svgMap[0] && this.svgMap[0].stands);
      if (fromMap) {
        this.directory = fromMap;
        this.markOccupiedStands();
        return;
      }

      // Запасной путь, пока поля `stands` нет у всех мероприятий
      this.getStandDirectory()
        .then((list) => {
          this.directory = directoryFromExhibitors(list);
          this.markOccupiedStands();
        })
        .catch(() => {
          this.directory = [];
        });
    },

    /** Номера стендов подставленной карты — по графу, если он сошёлся. */
    boothLabels() {
      if (!this.navRouter) return [];
      return this.navRouter
        .destinations()
        .filter((target) => target.kind === 'booth')
        .map((target) => target.label);
    },

    markOccupiedStands() {
      if (!this.mapIsReady || !this.$refs.mapRef_0) return;

      const occupied = new Set(
        this.directory.map((company) => normalizeStandNumber(company.stand)),
      );
      this.$refs.mapRef_0[0].applyDirectory(occupied);
    },

    /**
     * Включает навигацию, если пара сошлась. Не сошлась или графа нет —
     * модуль остаётся обычной картой, и это не ошибка: навигацию
     * размечают не на всех мероприятиях.
     */
    initNavigation(mapItem) {
      this.navRouter = null;
      if (!mapItem || !mapItem.nav_graph) return;

      const { ok, problems } = checkPair(mapItem.svg_map, mapItem.nav_graph, mapItem.map_name);
      if (!ok) {
        console.warn('[карта] навигация выключена, граф не сошёлся с картой:', problems.join('; '));
        return;
      }

      this.navRouter = createRouter(mapItem.nav_graph);
    },

    onMapReady(index) {
      if (index !== 0) return;
      this.mapIsReady = true;
      this.standNumbers = this.$refs.mapRef_0[0].standNumbers();
      this.markOccupiedStands();
    },

    /**
     * Следит за высотой нижней панели: она меняется не только со
     * сменой состояния, но и когда внутри формы разворачивают список
     * мест, — а состояние страницы при этом то же.
     */
    watchBottomPanel() {
      if (this.panelObserver) {
        this.panelObserver.disconnect();
        this.panelObserver = null;
      }

      const panel = this.$refs.bottomPanel;
      if (!panel) {
        this.setBottomInset(null);
        return;
      }

      this.setBottomInset(panel);
      if (typeof ResizeObserver === 'undefined') return;

      this.panelObserver = new ResizeObserver(() => this.setBottomInset(panel));
      this.panelObserver.observe(panel);
    },

    /**
     * Считаем по фактическому положению панели, а не по её высоте:
     * панель прилипает не вплотную к низу, и складывать отступы
     * вручную значит однажды разойтись со стилями.
     */
    setBottomInset(panel) {
      const grid = this.$refs.mapGrid;
      if (!grid) return;

      const taken = panel
        ? grid.getBoundingClientRect().bottom - panel.getBoundingClientRect().top
        : 0;

      // Кнопки зума уже подняты над низом карты — добавляем только то,
      // чего этим отступам не хватает, чтобы панель их не накрыла
      const needed = Math.max(0, (taken + PANEL_GAP) - ZOOM_BASE_OFFSET);

      // Выше строки поиска не поднимаем: развёрнутый список мест бывает
      // такой высоты, что кнопки уехали бы под неё
      const gridRect = grid.getBoundingClientRect();
      const search = this.$refs.mapSearch;
      const ceiling = search ? search.getBoundingClientRect().bottom : gridRect.top;
      const limit = Math.max(0, gridRect.bottom - ceiling - PANEL_GAP - ZOOM_STACK_TOP);

      grid.style.setProperty('--map-bottom-inset', `${Math.round(Math.min(needed, limit))}px`);
    },

    // ─── Прицел ──────────────────────────────────────────────
    startAiming(field) {
      this.aimingField = field;
      this.routeMessage = null;
      this.aimedTarget = null;
      this.$nextTick(this.pollAim);
    },

    /**
     * Что под прицелом, пересматриваем по кадрам: карта едет
     * трансформацией, и события о её остановке нет — а подпись должна
     * успевать за пальцем.
     */
    pollAim() {
      if (!this.aimingField) return;
      if (this.mapIsReady) {
        this.aimedTarget = this.$refs.mapRef_0[0].updateAim();
      }
      this.aimFrame = requestAnimationFrame(this.pollAim);
    },

    confirmAim() {
      if (!this.aimedTarget) return;

      const known = this.navRouter && this.navRouter.target(this.aimedTarget.id);
      this[this.aimingField === 'from' ? 'routeFrom' : 'routeTo'] = {
        text: this.aimedLabel,
        id: known ? known.id : null,
      };
      this.aimingField = null;
    },

    // ─── Маршрут ─────────────────────────────────────────────
    /**
     * Открывает форму. Точку старта спрашиваем один раз за сессию:
     * спрашивать «где вы?» перед каждым маршрутом — самый быстрый
     * способ сделать эту кнопку бесполезной.
     */
    openRouteForm(toStand = null) {
      const start = this.rememberedStart();

      this.routeFrom = { text: start || '', id: null };
      this.routeTo = toStand ? { text: toStand, id: `stand-${toStand}` } : { text: '', id: null };
      this.routeMessage = null;
      this.routeCollapsed = false;
      this.routeFormOpen = true;
      this.closeStandProps();
    },

    closeRouteForm() {
      this.routeFormOpen = false;
      this.routeCollapsed = false;
      this.aimingField = null;
      this.routeFrom = { text: '', id: null };
      this.routeTo = { text: '', id: null };
      this.routeMessage = null;
      if (this.mapIsReady) this.$refs.mapRef_0[0].hideRoute();
    },

    /** Поле формы → цель графа. Выбранное из списка помнит свой id. */
    resolveRouteField(field) {
      if (!field) return null;
      if (field.id) return this.navRouter.target(field.id);

      const text = (field.text || '').trim();
      return text ? this.navRouter.find(text) : null;
    },

    buildRoute({ from, to }) {
      if (!this.navRouter || !this.mapIsReady) return;

      const start = this.resolveRouteField(from);
      const finish = this.resolveRouteField(to);

      const missing = [[start, from], [finish, to]].find(([target]) => !target);
      if (missing) {
        this.routeMessage = this.$t('map.standUnknown', { stand: missing[1].text.trim() });
        return;
      }

      const route = this.navRouter.route(start.id, finish.id);
      if (!route) {
        this.routeMessage = this.$t('map.routeNotFound');
        return;
      }

      this.$refs.mapRef_0[0].showRoute(route);
      this.rememberStart(from.text.trim());
      this.routeMessage = null;
      this.routeCollapsed = true;
    },

    /**
     * Старт держим в sessionStorage: «за сессию» — это про вкладку, а
     * не навсегда. Экран пересоздаётся при возврате на него, поэтому в
     * данных компонента он бы не пережил переход в профиль и обратно.
     */
    storageKey() {
      return `exh-${this.$store.getters.getExhId}-route-start`;
    },

    rememberedStart() {
      try {
        return sessionStorage.getItem(this.storageKey()) || '';
      } catch (error) {
        return '';
      }
    },

    rememberStart(value) {
      if (!value) return;
      try {
        sessionStorage.setItem(this.storageKey(), value);
      } catch (error) {
        // приватный режим: маршрут построится, просто старт не запомнится
      }
    },
    updateFonts(fontLink) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'stylesheet');
      link.setAttribute('type', 'text/css');
      link.setAttribute('href', fontLink);
      document.head.appendChild(link);
    },
    /** Компании, числящиеся за стендом. Их может быть больше одной. */
    companiesAtStand(standID) {
      const wanted = normalizeStandNumber(standID);
      return this.directory.filter((company) => normalizeStandNumber(company.stand) === wanted);
    },

    /** Выбор из поиска: показываем стенд на карте и открываем карточку. */
    selectFound(item) {
      if (!item || !item.stand) return;
      if (this.mapIsReady) {
        this.$refs.mapRef_0[0].showStand(item.stand);
      }
      this.clickStand(`stand-${item.stand}`);
    },

    clickStand(stand) {
      // Карточку прежнего стенда закрываем, но подсветку не трогаем: её
      // только что поставил тот, кто нас позвал, — щелчок по самому стенду
      // или showStand из поиска. Раньше здесь звался closeStandProps, и он
      // сразу снимал свежую серую заливку с остальных стендов: на первом
      // поиске она держалась, а со второго пропадала, потому что карточка
      // уже была открыта.
      this.standProps = null;

      // Осиротело 21.09.2026: standsList объявлен в data и больше нигде не
      // заполняется, обход всегда по пустому. Снятие подсветки живёт рядом,
      // в clearHighlight у MapItem, и работает именно оно.
      this.standsList.forEach((itemStand) => {
        itemStand.classList.remove('active');
      });

      const standID = stand.replace('stand-', '');

      // Справочник уже собран, второй запрос за тем же не нужен — и так
      // карточка открывается без сети
      const [company] = this.companiesAtStand(standID);
      if (company) {
        this.showStand = standID;
        this.standProps = { ...company, stand: standID };
        return;
      }

      this.getExhibitorByStand(standID)
        .then((data) => {
          if (data) {
            this.showStand = standID;
            // Сюда попадаем, пока справочник не загрузился, — например, при
            // переходе «Показать стенд» из профиля. Ответ приводим к виду
            // справочника: в сыром `id` вместо `ident`, и ссылки не было.
            this.standProps = normalizeCompany(data, standID);
          } else if (this.$refs.mapRef_0[0].emptyStand) {
            this.$refs.mapRef_0[0].emptyStand(stand);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    closeStandProps() {
      if (this.$refs.mapRef_0 && this.$refs.mapRef_0[0].clearHighlight && this.standProps) {
        this.$refs.mapRef_0[0].clearHighlight();
      }
      this.standProps = null;
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.page {
  &__wrapper {
    height: 100%;
  }
}
.container-static {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: [start] 1fr [end];
  grid-template-rows: [start]  1fr [bottomSeparateLine] auto [end];
  position: relative;
  overflow: hidden;
}
// Поиск лежит поверх карты, а не занимает строку сетки: иначе он
// отъедал бы у зала высоту, а её на телефоне и так мало.
.map-search__wrapper {
  position: absolute;
  // По краям вровень с нижней панелью: она занимает всю ширину сетки,
  // и собственный отступ делал строку поиска уже неё
  top: 10px;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
// Квадратная кнопка рядом с поиском — как лупа и воронка в каталоге
.map-aim-bar {
  display: flex;
  flex-direction: column;
  gap: $padding-step * 3;
  color: $color-text;
  background: $color-base;
  box-shadow: 0 0 10px 3px rgba(0, 0, 0, 0.25);
  border-radius: $border-radius-inside-large;
  padding: $padding-step * 3;

  &__hint {
    min-height: 2.6em;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    line-height: 1.3em;
    text-align: center;
  }
}
.map-route-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  font-size: 22px;
  color: $color-text;
  background: $color-base;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.participant {
  &-info {
    &__wrapper {
      position: sticky;
      bottom: 10px;
      grid-column: [start] / [end];
      grid-row: bottomSeparateLine / [end];
    }
    position: relative;
    display: flex;
    gap: 5px;
    color: $color-text;
    background: $color-base;
    box-shadow: 0 0 10px 3px rgba(0, 0, 0, 0.25);
    padding: $padding-step * 2;
    border-radius: $border-radius-inside-large;

    // Крестик прижат к самому углу, а угол скруглён на 16px: при отступе
    // в 2px значок приходился на дугу и висел снаружи белого поля. Теперь
    // кнопка занимает угол целиком, а значок стоит в её середине — то есть
    // внутри скругления. Заодно область нажатия выросла с 16px до 36:
    // прежняя была меньше подушечки пальца.
    .close-btn {
      position: absolute;
      top: 0;
      right: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      font-size: 16px;
      border: none;
      background: transparent;
      padding: 0;
      color: $color-default;
    }
  }
  &-logo {
    flex: 0 0 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    & > img {
      max-width: 100%;
      max-height: 100%;
    }
  }
  &-about {
    flex: 1 1 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;

    &-stand {
      font-size: 1rem;
      font-weight: 600;
    }
    &-company {
      font-size: 1rem;
      line-height: 1.2em;
      font-weight: 400;
    }
  }
  &-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-top: .5rem;
  }
  // Кнопка в карточке узкая, поэтому не `up-btn` во всю ширину, но
  // цвета и толщина рамки те же — чтобы читалась как кнопка приложения
  &-route {
    display: flex;
    align-items: center;
    gap: $padding-step;
    border: 2px solid $color-primary;
    border-radius: $border-radius-inside;
    background: $color-base;
    padding: $padding-step $padding-step * 3;
    font-size: .9rem;
    color: $color-primary;

    & i {
      font-size: 1.1em;
    }
  }
  // Ссылка одна в строке, когда кнопки маршрута нет, — прижимаем вправо
  &-actions &-link {
    margin-left: auto;
  }

  &-about &-link {
    display: flex;
    justify-content: end;
    align-items: center;
    font-size: .9rem;
    color: $color-primary;
    text-decoration: none;

    & i {
      font-size: 1.1em;
    }
  }

  @media (min-width: 360px) {
    &-info {
      &__wrapper {
        gap: 15px;
      }
    }
    &-logo {
      @media (min-width: 360px) {
        flex: 0 0 100px;
      }
    }
  }
}
</style>
